import React, { useState } from "react";
import { Typography, Box } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { CustomTheme } from "../../../../types/customTheme";
import LicenseEditModal from "../LicenseDetails/LicenseEditModal";
import WithLoading from "../../../../utils/WithLoading";
import LicenseTable from "./LicenseTable";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import LicenseCreateModal from "./LicenseCreateModal";
import ChargebeeCustomerCard from "./ChargebeeCustomerCard";
import ChargebeeCustomerModal from "./ChargebeeCustomerModal";
import LicenseSubscriptionIdModal from "./LicenseSubscriptionIdModal";
import LicenseRemoveModal from "./LicenseRemoveModal";
import AlertDialogDefault from "../../../../components/alerts/AlertDialogDefault";

interface License {
  licenseCode: string;
  name: string;
  deviceType: string;
  deviceId: string;
  status: string;
  subscriptionPeriod: string;
  subscriptionAmount: string;
  subscriptionId?: string;
}

interface ChargebeeCustomer {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    noData: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(2),
      fontSize: "44px",
      textAlign: "center",
      fontStyle: "bold",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(4),
    },
  })
);

interface Props {
  locationId?: string;
  licenses: License[];
  activePassLicenseCodes: string[];
  loading: boolean;
  error: string;
  onReload: () => void | Promise<void>;
  onLicenseUpdate: (licenseCode: string, patch: Partial<License>) => void;

  chargebeeCustomerId: string | null;
  chargebeeCustomer: ChargebeeCustomer | null;
  chargebeeLoading: boolean;
  chargebeeError: string;
  onConnectChargebee: () => void;
  onDisconnectChargebee: () => void | Promise<void>;

  chargebeeModalOpen: boolean;
  onCloseChargebeeModal: () => void;
  chargebeeCustomers: ChargebeeCustomer[];
  chargebeeCustomersLoading: boolean;
  onSelectChargebeeCustomer: (customer: ChargebeeCustomer) => void;
}

const LicenseDetails: React.FC<Props> = ({
  locationId,
  licenses,
  activePassLicenseCodes,
  loading,
  error,
  onReload,
  onLicenseUpdate,

  chargebeeCustomerId,
  chargebeeCustomer,
  chargebeeLoading,
  chargebeeError,
  onConnectChargebee,
  onDisconnectChargebee,

  chargebeeModalOpen,
  onCloseChargebeeModal,
  chargebeeCustomers,
  chargebeeCustomersLoading,
  onSelectChargebeeCustomer,
}) => {
  const classes = useStyles();

  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [selectedRemoveLicense, setSelectedRemoveLicense] = useState<License | null>(null);
  const [bulkLinkLicenses, setBulkLinkLicenses] = useState<License[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [subscriptionIdModalOpen, setSubscriptionIdModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [disconnectMessage, setDisconnectMessage] = useState("");

  const unlinkedLicenses = (licenses || []).filter(
    (license) => !String(license.subscriptionId || "").trim()
  );
  const showConnectAllUnlinked =
    Boolean(chargebeeCustomerId) && unlinkedLicenses.length > 1;

  const handleRowClick = (license: License) => {
    setSubscriptionIdModalOpen(false);
    setBulkLinkLicenses([]);
    setSelectedLicense(license);
    setEditModalOpen(true);
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
  };

  const handleCreateClick = () => {
    setCreateModalOpen(true);
  };

  const handleChargebeeLink = (license: License) => {
    setEditModalOpen(false);
    setBulkLinkLicenses([]);
    setSelectedLicense(license);
    setSubscriptionIdModalOpen(true);
  };

  const handleConnectAllUnlinked = () => {
    setDisconnectMessage("");
    setEditModalOpen(false);
    setSelectedLicense(null);
    setBulkLinkLicenses(unlinkedLicenses);
    setSubscriptionIdModalOpen(true);
  };

  const handleDisconnectChargebee = async () => {
    const hasLinkedSubscription = (licenses || []).some((license) =>
      Boolean(String(license.subscriptionId || "").trim())
    );

    if (hasLinkedSubscription) {
      setDisconnectMessage(
        "Please unlink all subscriptions before disconnecting this account."
      );
      return;
    }

    setDisconnectMessage("");
    await onDisconnectChargebee();
  };

  const handleOpenRemoveModal = (license: License) => {
    setEditModalOpen(false);
    setSelectedRemoveLicense(license);
    setRemoveModalOpen(true);
  };

  const handleSubscriptionIdModalClose = () => {
    setSubscriptionIdModalOpen(false);
    setBulkLinkLicenses([]);
  };

  return (
    <Box p={4}>
      <Box className={classes.headerRow}>
        <Typography variant="h4" align="left">
          Licenses & Subscriptions
        </Typography>

        <ButtonCommon
          variant="contained"
          color="green"
          onClick={handleCreateClick}
          isDisabled={!chargebeeCustomerId}
          style={{ minWidth: 180, height: 36 }}
        >
          Create License
        </ButtonCommon>
      </Box>

      {error && (
        <Typography style={{ color: "red", marginBottom: 16 }}>
          {error}
        </Typography>
      )}

      <Box mt={3}>
        <ChargebeeCustomerCard
          customerId={chargebeeCustomerId}
          customer={chargebeeCustomer}
          loading={loading || chargebeeLoading}
          error={chargebeeError}
          onConnect={onConnectChargebee}
          showConnectAllUnlinked={showConnectAllUnlinked}
          onConnectAllUnlinked={handleConnectAllUnlinked}
          onDisconnect={handleDisconnectChargebee}
        />
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column">
          {[...Array(6)].map((_, index) => (
            <Box key={index} mb={1}>
              <Skeleton
                variant="rect"
                height={32}
                style={{ borderRadius: 10 }}
              />
            </Box>
          ))}
        </Box>
      ) : !licenses || licenses.length === 0 ? (
        <Typography className={classes.noData}>No licenses found.</Typography>
      ) : (
        <LicenseTable
          licenses={licenses}
          activePassLicenseCodes={activePassLicenseCodes}
          onRowClick={handleRowClick}
          onChargebeeLink={handleChargebeeLink}
        />
      )}

      <LicenseEditModal
        open={editModalOpen}
        onClose={handleModalClose}
        license={selectedLicense}
        locationId={locationId || ""}
        onUpdate={onLicenseUpdate}
        onReload={onReload}
        onRequestRemove={handleOpenRemoveModal}
      />

      <LicenseCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        locationId={locationId || ""}
        chargebeeCustomerId={chargebeeCustomerId}
        onUpdate={onReload}
      />

      <LicenseSubscriptionIdModal
        open={subscriptionIdModalOpen}
        onClose={handleSubscriptionIdModalClose}
        license={selectedLicense}
        locationId={locationId || ""}
        chargebeeCustomerId={chargebeeCustomerId}
        onUpdate={onLicenseUpdate}
        bulkLicenses={bulkLinkLicenses}
      />

      <LicenseRemoveModal
        open={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        license={selectedRemoveLicense}
        locationId={locationId || ""}
        onRemoved={() => {
          setRemoveModalOpen(false);
          setSelectedRemoveLicense(null);
          onReload();
        }}
      />

      <ChargebeeCustomerModal
        open={chargebeeModalOpen}
        onClose={onCloseChargebeeModal}
        customers={chargebeeCustomers}
        loading={chargebeeCustomersLoading}
        onSelect={onSelectChargebeeCustomer}
      />

      <AlertDialogDefault
        open={Boolean(disconnectMessage)}
        title="Disconnect from Chargebee"
        desc={disconnectMessage}
        severity="error"
        disableBackdropClick
        confirmLabel="OK"
        confirmAction={() => setDisconnectMessage("")}
      />
    </Box>
  );
};

export default WithLoading(LicenseDetails);
